import { useState, useCallback } from 'react'
import { useAppStore } from '../../store/appStore'
import { apiUrl, authFetch } from '../../lib/api'

interface FileChange { path: string; content: string; action: 'write' | 'delete' }

export function ComposerPanel({ onClose }: { onClose: () => void }) {
  const [prompt, setPrompt] = useState('')
  const [loading, setLoading] = useState(false)
  const [changes, setChanges] = useState<FileChange[]>([])
  const [streamText, setStreamText] = useState('')
  const [selectedFile, setSelectedFile] = useState<string | null>(null)
  const [applied, setApplied] = useState(false)

  const fetchWorkspaceContext = useCallback(async () => {
    const { openTabs, activeTabId } = useAppStore.getState()
    const files: { path: string; content: string }[] = []
    const activeTab = openTabs.find(t => t.id === activeTabId)
    if (activeTab) files.push({ path: activeTab.path.replace(/^\//, ''), content: activeTab.content })
    for (const tab of openTabs) {
      if (tab.id === activeTabId) continue
      files.push({ path: tab.path.replace(/^\//, ''), content: tab.content.slice(0, 2000) })
    }
    try {
      const res = await authFetch(apiUrl('/api/files/list'))
      if (res.ok) {
        const tree = await res.json()
        const paths: string[] = []
        const collect = (nodes: any[], prefix = '') => {
          for (const n of nodes) {
            const p = prefix ? `${prefix}/${n.name}` : n.name
            paths.push(n.type === 'folder' ? `${p}/` : p)
            if (n.children) collect(n.children, p)
          }
        }
        collect(tree)
        files.push({ path: '__project_tree__', content: paths.join('\n') })
      }
    } catch {}
    return files
  }, [])

  const handleCompose = async () => {
    if (!prompt.trim() || loading) return
    setLoading(true); setChanges([]); setStreamText(''); setApplied(false)

    const contextFiles = await fetchWorkspaceContext()

    try {
      const res = await authFetch(apiUrl('/api/chat/stream'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [{ role: 'user', content: prompt }],
          context: { mode: 'agent', files: contextFiles },
        }),
      })

      if (!res.ok || !res.body) throw new Error('Failed')
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let full = '', buf = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })
        const lines = buf.split('\n'); buf = lines.pop() ?? ''
        for (const line of lines) {
          if (!line.trim().startsWith('data: ')) continue
          try {
            const data = JSON.parse(line.trim().slice(6))
            if (data.text) { full += data.text; setStreamText(full) }
            if (data.done) {
              // Parse file operations from response
              const parsed = parseFileOps(full)
              setChanges(parsed)
              if (parsed.length > 0) setSelectedFile(parsed[0].path)
            }
          } catch {}
        }
      }
      if (full && changes.length === 0) {
        const parsed = parseFileOps(full)
        setChanges(parsed)
        if (parsed.length > 0) setSelectedFile(parsed[0].path)
      }
    } catch {} finally { setLoading(false) }
  }

  const applyAll = async () => {
    for (const change of changes) {
      const store = useAppStore.getState()
      const existingTab = store.openTabs.find(t => t.path === `/${change.path}`)
      if (existingTab) store.snapshotFile(`/${change.path}`, existingTab.content)

      if (change.action === 'delete') {
        if (existingTab) store.closeTab(existingTab.id)
      } else {
        if (existingTab) {
          store.markDirty(existingTab.id, change.content)
        } else {
          store.openTab({
            id: `/${change.path}`, path: `/${change.path}`,
            name: change.path.split('/').at(-1) ?? change.path,
            language: inferLang(change.path), content: change.content,
          })
        }
      }
      try {
        const endpoint = change.action === 'delete' ? '/api/files/delete' : '/api/files/write'
        await authFetch(apiUrl(endpoint), {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ path: change.path, content: change.content }),
        })
      } catch {}
    }
    window.dispatchEvent(new CustomEvent('pulse:files-changed'))
    setApplied(true)
  }

  const selectedChange = changes.find(c => c.path === selectedFile)

  return (
    <div style={{
      position: 'fixed', inset: 40, background: 'var(--bg-1)',
      border: '1px solid var(--border-2)', borderRadius: 14,
      boxShadow: '0 16px 64px rgba(0,0,0,0.6)', zIndex: 1000,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 14 }}>🎼</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 13, color: 'var(--text-0)' }}>Composer</span>
        <span style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>Multi-file AI agent</span>
        <div style={{ flex: 1 }} />
        {changes.length > 0 && !applied && (
          <button onClick={applyAll} style={{ padding: '6px 16px', borderRadius: 8, border: 'none', background: 'var(--accent-2)', color: '#000', cursor: 'pointer', fontSize: 11, fontFamily: 'var(--font-mono)', fontWeight: 700 }}>
            ✓ Apply All ({changes.length} files)
          </button>
        )}
        {applied && <span style={{ color: 'var(--accent-2)', fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700 }}>✓ Applied!</span>}
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 16 }}>✕</button>
      </div>

      {/* Input */}
      <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 8 }}>
        <textarea
          value={prompt} onChange={e => setPrompt(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleCompose() } }}
          placeholder="Describe multi-file changes... (e.g. 'Add auth middleware to all API routes')"
          style={{
            flex: 1, padding: 10, borderRadius: 8, border: '1px solid var(--border)',
            background: 'var(--bg-0)', color: 'var(--text-0)', fontSize: 12,
            fontFamily: 'var(--font-mono)', resize: 'none', height: 50, outline: 'none',
          }}
        />
        <button onClick={handleCompose} disabled={loading || !prompt.trim()} style={{
          padding: '0 20px', borderRadius: 8, border: 'none',
          background: 'var(--accent)', color: '#fff', cursor: 'pointer',
          fontSize: 12, fontFamily: 'var(--font-mono)', fontWeight: 700,
          opacity: loading ? 0.5 : 1, alignSelf: 'stretch',
        }}>
          {loading ? 'Composing...' : 'Compose'}
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* File tree */}
        <div style={{ width: 220, borderRight: '1px solid var(--border)', overflow: 'auto', flexShrink: 0 }}>
          <div style={{ padding: '8px 12px', fontSize: 9, fontFamily: 'var(--font-mono)', color: 'var(--text-2)', fontWeight: 700 }}>
            FILES TO CHANGE ({changes.length})
          </div>
          {changes.map(c => (
            <div key={c.path} onClick={() => setSelectedFile(c.path)} style={{
              padding: '6px 12px', cursor: 'pointer', fontSize: 11, fontFamily: 'var(--font-mono)',
              background: selectedFile === c.path ? 'rgba(124,110,245,0.1)' : 'none',
              color: selectedFile === c.path ? 'var(--accent)' : 'var(--text-1)',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <span style={{ color: c.action === 'delete' ? 'var(--red)' : 'var(--accent-2)', fontSize: 9 }}>
                {c.action === 'delete' ? 'D' : 'M'}
              </span>
              {c.path.split('/').at(-1)}
            </div>
          ))}
          {changes.length === 0 && loading && (
            <div style={{ padding: 12, fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>
              Generating changes...
            </div>
          )}
        </div>

        {/* Preview */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {selectedChange ? (
            <pre style={{ margin: 0, padding: 14, fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-1)', lineHeight: 1.6 }}>
              <div style={{ color: 'var(--text-2)', marginBottom: 8, fontSize: 10 }}>// {selectedChange.path}</div>
              {selectedChange.content}
            </pre>
          ) : loading && streamText ? (
            <pre style={{ margin: 0, padding: 14, fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-2)', lineHeight: 1.6 }}>
              {streamText}
            </pre>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-2)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
              {changes.length > 0 ? 'Select a file to preview changes' : 'Describe what you want to change across multiple files'}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function parseFileOps(text: string): FileChange[] {
  const changes: FileChange[] = []
  const regex = /```\w*\n([\s\S]*?)```/g
  let match
  while ((match = regex.exec(text)) !== null) {
    const block = match[1]
    const fileMatch = block.match(/^\/\/\s*FILE:\s*(.+)/m)
    if (!fileMatch) continue
    const path = fileMatch[1].trim().replace(/^\.?\//, '')
    const actionMatch = block.match(/^\/\/\s*ACTION:\s*(delete|remove)/mi)
    const action = actionMatch ? 'delete' as const : 'write' as const
    const content = block.replace(/^\/\/\s*FILE:.*\n/, '').replace(/^\/\/\s*ACTION:.*\n/, '').trimEnd()
    changes.push({ path, content, action })
  }
  return changes
}

function inferLang(path: string): string {
  const ext = path.split('.').at(-1)?.toLowerCase() ?? ''
  const map: Record<string, string> = { ts: 'ts', tsx: 'tsx', js: 'js', jsx: 'jsx', json: 'json', css: 'css', html: 'html', md: 'md', py: 'py' }
  return map[ext] ?? 'text'
}
